import type { AxiosInstance } from "axios";

import { parseAxiosResponse } from "@thalamos/common";
import type {
  CreateOrganisationRequest,
  GetAvailableOrganisationsResponse,
  OrganisationConfigurationHierarchyUnbranded,
  OrganisationConfigurationJson,
  OrganisationId,
  SearchOrganisationsResponse,
  UpdateOrganisationRequest,
} from "@thalamos/vision-common";
import {
  GetAvailableOrganisationsResponseSchema,
  SearchOrganisationsResponseSchema,
} from "@thalamos/vision-common";

import { useApiAxios } from "../axios/api.js";

const parseResponseGetAvailableOrganisations = parseAxiosResponse(
  GetAvailableOrganisationsResponseSchema,
);

const parseResponseSearchOrganisations = parseAxiosResponse(
  SearchOrganisationsResponseSchema,
);

export const config = {
  organisation: {
    getAvailableOrganisations: (id: OrganisationId) => ({
      url: `/api/organisation/${id}/available-organisations`,
      method: "get",
      transformResponse: parseResponseGetAvailableOrganisations,
    }),
    search: (query: string, limit: number = 10, offset: number = 0) => {
      return {
        url: `/api/organisation/search`,
        method: "get",
        params: {
          query,
          limit,
          offset,
        },
        transformResponse: parseResponseSearchOrganisations,
      };
    },
    getOrgConfig: (id: OrganisationId) => ({
      url: `/api/configuration/organisations/${id}`,
      method: "get",
    }),
    getOrgConfigHierarchy: (id: OrganisationId) => ({
      url: `/api/configuration/organisations/${id}/hierarchy`,
      method: "get",
    }),
    setOrgConfig: (
      id: OrganisationId,
      data: OrganisationConfigurationJson,
    ) => ({
      url: `/api/configuration/organisations/${id}`,
      method: "post",
      data,
    }),
    update: (id: OrganisationId, org: UpdateOrganisationRequest) => ({
      url: `/api/organisation/${id}`,
      method: "put",
      data: org,
    }),
    create: (id: OrganisationId, org: CreateOrganisationRequest) => ({
      url: `/api/organisation/${id}`,
      method: "post",
      data: org,
    }),
    delete: (id: OrganisationId) => ({
      url: `/api/organisation/${id}/delete`,
      method: "post",
    }),
  },
};

export const api = (apiAxios: AxiosInstance) => {
  return {
    organisation: {
      getAvailableOrganisations: (id: OrganisationId) =>
        apiAxios.request<GetAvailableOrganisationsResponse>(
          config.organisation.getAvailableOrganisations(id),
        ),
      search: (query: string, limit: number = 10, offset: number = 0) =>
        apiAxios.request<SearchOrganisationsResponse>(
          config.organisation.search(query, limit, offset),
        ),
      setOrgConfig: (id: OrganisationId, data: OrganisationConfigurationJson) =>
        apiAxios.request(config.organisation.setOrgConfig(id, data)),
      update: (id: OrganisationId, org: UpdateOrganisationRequest) =>
        apiAxios.request(config.organisation.update(id, org)),
      create: (id: OrganisationId, org: CreateOrganisationRequest) =>
        apiAxios.request(config.organisation.create(id, org)),
      delete: (id: OrganisationId) =>
        apiAxios.request(config.organisation.delete(id)),
    },
  };
};

export const apiHooks = () => {
  return {
    organisation: {
      search: (query: string, limit: number = 10, offset: number = 0) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return useApiAxios<SearchOrganisationsResponse>(
          config.organisation.search(query, limit, offset),
        );
      },
      getAvailableOrganisations: (id: OrganisationId) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return useApiAxios<GetAvailableOrganisationsResponse>(
          config.organisation.getAvailableOrganisations(id),
        );
      },
      getOrgConfig: (id: OrganisationId) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return useApiAxios<OrganisationConfigurationJson>(
          config.organisation.getOrgConfig(id),
        );
      },
      getOrgConfigHierarchy: (id: OrganisationId) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return useApiAxios<OrganisationConfigurationHierarchyUnbranded>(
          config.organisation.getOrgConfigHierarchy(id),
        );
      },
    },
  };
};
